import { DynamicPageVariant } from '@superfunnel/types';

export const getWeightInPercentage = (
  variant: DynamicPageVariant,
  websiteId: string,
): number => {
  const weights = getWeightsInPercentage(
    variant.websiteIds || [],
    variant.websiteIdsWeights || {},
  );

  const websiteWeight = weights.find((w) => w.websiteId === websiteId);

  return websiteWeight
    ? websiteWeight.weight
    : 100 / (variant.websiteIds.length ?? 1);
};

export const getWeightsInPercentage = (
  websiteIds: string[],
  websiteIdsWeights: NonNullable<DynamicPageVariant['websiteIdsWeights']>,
) => {
  // Filter and normalize weights
  const filteredWeights = Object.entries(websiteIdsWeights).filter(
    ([websiteId]) => websiteIds.includes(websiteId),
  );

  if (filteredWeights.length > 0) {
    // Calculate total of existing weights
    const totalWeight = filteredWeights.reduce(
      (sum, [, weight]) => sum + (weight || 0),
      0,
    );

    // Normalize existing weights to sum to 100
    const normalizedWeights = filteredWeights.map(([websiteId, weight]) => ({
      websiteId,
      weight: totalWeight > 0 ? ((weight || 0) / totalWeight) * 100 : 0,
    }));

    if (normalizedWeights.length === websiteIds.length) {
      // All websites have weights
      return normalizedWeights;
    } else {
      // Some websites need new weights
      const existingWeightSum = normalizedWeights.reduce(
        (sum, { weight }) => sum + weight,
        0,
      );
      const remainingWeight = Math.max(0, 100 - existingWeightSum);
      const newSitesCount = websiteIds.length - normalizedWeights.length;
      const weightPerNewSite =
        newSitesCount > 0 ? remainingWeight / newSitesCount : 0;

      const allWeights = [
        ...normalizedWeights,
        ...websiteIds
          .filter((id) => !normalizedWeights.find((w) => w.websiteId === id))
          .map((websiteId) => ({
            websiteId,
            weight: weightPerNewSite,
          })),
      ];

      return allWeights;
    }
  } else {
    // No existing weights - distribute equally
    const equalWeight = websiteIds.length > 0 ? 100 / websiteIds.length : 100;
    const initialWeights = websiteIds.map((websiteId) => ({
      websiteId,
      weight: equalWeight,
    }));

    if (initialWeights.length === 0) {
      initialWeights.push({ websiteId: 'default', weight: 100 });
    }

    return initialWeights;
  }
};
